// Page-to-page navigation with barba.js. The header, footer, reading-list
// modal, cookie banner and toast stay mounted; only <main data-barba="container">
// is swapped, under a two-layer wipe (accent, then panel with the brand mark).
import barba from "@barba/core";
import { gsap, ScrollTrigger, reducedMotion } from "./motion.js";

const $ = (s, c = document) => c.querySelector(s);

const wipe = {
  el: () => $(".js-pt"),
  layers: () => Array.from(document.querySelectorAll(".js-pt .page-transition__layer")),
  mark: () => $(".js-pt .page-transition__mark"),
};

const instant = { duration: 0.01 };

// Everything outside <main> that depends on the page: <title>, <body> class,
// the menu's current-item state and the SEO meta tags.
function syncChrome(html) {
  const next = new DOMParser().parseFromString(html, "text/html");

  document.title = next.title;
  document.body.className = next.body.className;

  const nav = $("#nav");
  const nextNav = next.querySelector("#nav");
  if (nav && nextNav) nav.innerHTML = nextNav.innerHTML;
  nav && nav.classList.remove("is-open");
  const burger = $(".js-burger");
  if (burger) burger.setAttribute("aria-expanded", "false");

  const metaSelector = 'meta[name="description"], meta[property^="og:"], meta[name^="twitter:"]';
  document.head.querySelectorAll(metaSelector).forEach((m) => m.remove());
  next.head.querySelectorAll(metaSelector).forEach((m) => document.head.appendChild(m.cloneNode(true)));
}

// Contact Form 7 binds on DOMContentLoaded only — re-bind forms that arrive via barba.
function rebindForms(container) {
  if (window.wpcf7 && typeof window.wpcf7.init === "function") {
    container.querySelectorAll(".wpcf7 > form").forEach((form) => window.wpcf7.init(form));
  }
}

function scrollToHash() {
  if (location.hash.length > 1) {
    let target = null;
    try {
      target = document.getElementById(decodeURIComponent(location.hash.slice(1)));
    } catch (e) {
      /* malformed hash */
    }
    if (target) {
      target.scrollIntoView({ behavior: "instant", block: "start" });
      return;
    }
  }
  window.scrollTo({ top: 0, behavior: "instant" });
}

/**
 * @param {object} hooks
 * @param {(container: Element, opts: {delay:number}) => void} hooks.mount    build the page (modules + animations)
 * @param {() => void} hooks.unmount  tear the previous page down
 */
export function initTransitions({ mount, unmount }) {
  const layers = wipe.layers();
  gsap.set(layers, { yPercent: 100 });

  barba.init({
    timeout: 8000,
    prevent: ({ el, href }) => {
      if (!el || !href) return false;
      if (el.closest(".no-barba, #wpadminbar") || el.hasAttribute("download") || el.target === "_blank") return true;
      let url;
      try {
        url = new URL(href, location.href);
      } catch (e) {
        return true;
      }
      if (/\/(wp-admin|wp-login\.php|wp-json|wp-content|feed)\b/.test(url.pathname) || /\.(pdf|zip|jpe?g|png|gif|webp|svg|mp4)$/i.test(url.pathname)) return true;
      // "#anchor" on the page we're already on: let the browser scroll.
      return url.pathname === location.pathname && url.search === location.search && url.hash !== "";
    },
    transitions: [
      {
        name: "wipe",

        // First load: no wipe, just the page intro.
        once({ next }) {
          mount(next.container, { delay: 0.15 });
        },

        leave({ current }) {
          if (reducedMotion()) return gsap.to(current.container, { autoAlpha: 0, ...instant });

          const pt = wipe.el();
          const tl = gsap.timeline();
          pt.classList.add("is-active");
          gsap.set(wipe.layers(), { yPercent: 100 });
          gsap.set(wipe.mark(), { scale: 0.4, rotate: -90, autoAlpha: 0 });

          tl.to(current.container, { y: -50, autoAlpha: 0.4, duration: 0.6, ease: "power3.in" }, 0)
            .to(layers[0], { yPercent: 0, duration: 0.65, ease: "power4.inOut" }, 0)
            .to(layers[1], { yPercent: 0, duration: 0.65, ease: "power4.inOut" }, 0.12)
            .to(wipe.mark(), { scale: 1, rotate: 0, autoAlpha: 1, duration: 0.55, ease: "back.out(1.7)" }, 0.55);
          return tl;
        },

        beforeEnter({ current, next }) {
          // Previous page: kill its triggers/listeners, take it out of layout.
          unmount();
          if (current.container && current.container.isConnected) current.container.style.display = "none";

          syncChrome(next.html);
          scrollToHash();
          mount(next.container, { delay: reducedMotion() ? 0 : 0.55 });
          rebindForms(next.container);
        },

        enter({ next }) {
          if (reducedMotion()) {
            return gsap.fromTo(next.container, { autoAlpha: 0 }, { autoAlpha: 1, ...instant });
          }
          const pt = wipe.el();
          const tl = gsap.timeline({
            onComplete: () => {
              pt.classList.remove("is-active");
              gsap.set(wipe.layers(), { yPercent: 100 });
            },
          });
          tl.to(wipe.mark(), { scale: 0.6, autoAlpha: 0, duration: 0.3, ease: "power2.in" }, 0.15)
            .to(layers[1], { yPercent: -100, duration: 0.75, ease: "power4.inOut" }, 0.25)
            .to(layers[0], { yPercent: -100, duration: 0.75, ease: "power4.inOut" }, 0.38);
          return tl;
        },

        afterEnter() {
          ScrollTrigger.refresh();
        },
      },
    ],
  });
}
